/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import {
  getUserFullName,
  getUserPosition,
  getUserCompany,
} from '@braindate/domain/lib/user/util';

import styles from './UserBanner.style';
import UserAvatar, {
  small as avatarSmall,
  medium as avatarMedium,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import messages from 'src/shared/domain/user/l10n/userL10n';

import type { User } from '@braindate/domain/lib/user/type';

type Props = {
  user: User,
  variant?: typeof small,
  withIcon?: boolean,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseThemeStyles(styles);

const UserBanner = ({ user, variant, withIcon, intl }: Props): React.Node => {
  const { locale } = intl;
  const name = getUserFullName(user);
  const position = getUserPosition(user, locale);
  const company = getUserCompany(user, locale);
  const title =
    position && company
      ? intl.formatMessage(messages.fullPosition, {
          position,
          company,
        })
      : position || company;

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles();

  const textClasses = classNames(classes.text, {
    [`${classes['text-withIcon']}`]: withIcon,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <div className={classes.root}>
      <UserAvatar
        user={user}
        variant={variant === small ? avatarSmall : avatarMedium}
        showOnlineBadge
      />
      <div className={textClasses}>
        <span className={classes.name}>{name}</span>
        <span className={classes.title}>{title}</span>
      </div>
    </div>
  );
};

export const small = Symbol();

export default compose(React.memo, injectIntl)(UserBanner);
